import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Loader2, Save, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Reusable booking-policy editor for enhancers ("/enhancer/me/booking-policy")
// and location managers ("/manager/me/booking-policy"). Precedence at booking time:
// enhancer → location → global default. Leaving a field blank/0 = inherit.
const FIELDS = [
  { key: "min_notice_hours", label: "Minimum notice (hours)", hint: "How far ahead clients must book. 0 = inherit." },
  { key: "deposit_pct", label: "Member deposit (%)", hint: "Deposit members pay to hold a slot. 0 = inherit." },
  { key: "cancel_window_hours", label: "Free cancellation window (hours)", hint: "Cancel this far ahead with no fee. 0 = inherit." },
  { key: "cancel_fee", label: "Late cancellation fee ($)", hint: "Charged if cancelled inside the window." },
  { key: "reschedule_window_hours", label: "Reschedule window (hours)", hint: "How far ahead a reschedule is allowed. 0 = inherit." },
];

export default function BookingPolicyEditor({ base = "enhancer", testid = "booking-policy" }) {
  const endpoint = base === "manager" ? "/manager/me/booking-policy" : "/enhancer/me/booking-policy";
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["booking-policy", base],
    queryFn: () => api.get(endpoint).then((r) => r.data),
    onSuccess: (d) => setForm({ ...(d.policy || {}) }),
  });

  const f = form ?? (data?.policy || {});
  const set = (k, v) => setForm({ ...f, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      FIELDS.forEach(({ key }) => {
        const v = f[key];
        if (v !== "" && v !== null && v !== undefined) payload[key] = Number(v);
      });
      const { data: res } = await api.put(endpoint, payload);
      setForm({ ...(res.policy || {}) });
      toast.success("Booking policy saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (isLoading) return <div className="p-6 text-center text-[#8FA89B]"><Loader2 className="animate-spin inline" /> Loading…</div>;

  return (
    <div className="eyw-card p-6" data-testid={`${testid}-editor`}>
      <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2 mb-1"><CalendarClock size={18} className="text-[#144F75]" /> Booking policy</h3>
      <p className="text-sm text-[#4A5D6B] mb-1">Set your own booking rules. {base === "manager" ? "These apply to your center" : "These apply to your appointments"} and override the platform default.</p>
      <p className="text-xs text-[#8FA89B] flex items-center gap-1 mb-5"><Info size={12} /> Priority when a client books: enhancer setting → center setting → platform default. Leave a field at 0/blank to inherit.</p>
      <div className="grid md:grid-cols-2 gap-4">
        {FIELDS.map(({ key, label, hint }) => (
          <div key={key}>
            <label className="text-sm font-medium text-[#1A242B] mb-1 block">{label}</label>
            <input type="number" min="0" step={key === "cancel_fee" ? "0.01" : "1"}
              value={f[key] ?? ""} onChange={(e) => set(key, e.target.value)}
              data-testid={`${testid}-${key}`} className="eyw-input w-full" placeholder="0" />
            <p className="text-[11px] text-[#8FA89B] mt-1">{hint}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-5">
        <button onClick={save} disabled={saving} data-testid={`${testid}-save`} className="eyw-btn !py-2.5 text-sm disabled:opacity-60">
          {saving && <Loader2 className="animate-spin" size={16} />} <Save size={15} /> Save policy
        </button>
      </div>
    </div>
  );
}
